import { Injectable } from "@angular/core";
import { map, Observable, of } from "rxjs";

import { AgentCompany, AgentFilterPayload, SupervisedAgentList, } from "src/app/models/agent";
import { StoreNames } from "src/app/constants/storage-names";

import { SessionService } from './storage/session.service';
import { ApiService } from "./api.service";
import { SessionStatus } from "src/app/constants/session";
import { CompanyResources } from "src/app/models/resource-link";
import { AgentAppendCallLogPayload, AgentCallLog, AgentCallLogs, CallLogPayload, CallLogSearchPayload } from "src/app/models/calllog";

import { ToastType } from 'src/app/constants/toast-type';
import { ToastService } from './toast.service';
import { ReassignScheduledEventListPayload, ScheduledEvent, ScheduledEventAppendPayload, ScheduledEventCompletePayload, ScheduledEventPayload, ScheduledEventSearchPayload, ScheduledEventsList, SupervisedAgent } from "src/app/models/scheduled-event";
import { Report, ReportsPayload } from "src/app/models/reports";
import { ApiHeaderType } from "src/app/constants/api-header-type";
import { ActivityLog, ActivityLogAppendPayload, ActivityLogCreatePayload, ActivityLogPayloadId, MemberActivityLogs } from "src/app/models/activity-log";

const controllerRoute = '/agents-crm';

@Injectable()
export class AgentCRMService {

    public agentCompanyResources: CompanyResources;
    public supervisedAgents: SupervisedAgent[];
    public sessionStore: Observable<any>;

    constructor(
        private apiService: ApiService,
        private sessionService: SessionService,
        private toastService: ToastService,
    ) {
        this.sessionStore = this.sessionService.$SessionClear;
        this.sessionStore.subscribe((status: string) => {
            switch (status) {
                case SessionStatus.initalize:
                    let sessionState = this.sessionService.retrieve(StoreNames.agentCompanyResources);
                    if (sessionState)
                        this.agentCompanyResources = sessionState;
                    break;

                case SessionStatus.clearSession:
                    this.agentCompanyResources = null;
                    this.supervisedAgents = null;
                    break;
            }
        });
    }

    public getCompanyResourcesByID(payload?: AgentCompany): Observable<CompanyResources> {
        if (!payload ||
            this.agentCompanyResources && this.agentCompanyResources.companyID == payload.companyID)
            return of(this.agentCompanyResources);

        return this.apiService.post(`${controllerRoute}/company-resources`, { companyID: payload.companyID }).pipe(
            map((companyResources: CompanyResources) => {
                this.agentCompanyResources = {
                    companyID: payload.companyID,
                    companyName: payload.companyName,
                    benefit: companyResources.benefit,
                    company: companyResources.company,
                    compliance: companyResources.compliance,
                    contacts: companyResources.contacts,
                };

                this.sessionService.store(StoreNames.agentCompanyResources, this.agentCompanyResources);
                return this.agentCompanyResources;
            }),
        );
    }

    public getCallLogs(payload: CallLogSearchPayload): Observable<AgentCallLog[]> {
        return this.apiService.post(`${controllerRoute}/call-log-specification`, payload).pipe(
            map((callLogs: AgentCallLogs) => {
                return callLogs.companyCallLog;
            }),
        );
    }

    public createCallLog(payload: CallLogPayload): Observable<AgentCallLog> {
        return this.apiService.post(`${controllerRoute}/call-log`, payload).pipe(
            map((callLog: AgentCallLog) => {
                this.toastService.buildToastMessage(ToastType.success, "Saved", "Call Log Created!");
                return callLog;
            }),
        );
    }

    public appendCallLog(payload: AgentAppendCallLogPayload): Observable<AgentCallLog> {
        return this.apiService.put(`${controllerRoute}/call-log`, payload).pipe(
            map((callLog: AgentCallLog) => {
                this.toastService.buildToastMessage(ToastType.success, "Saved", "Call Log Updated!");
                return callLog;
            }),
        );
    }

    public getSupervisedAgents(payload: AgentFilterPayload): Observable<SupervisedAgent[]> {
        if (this.supervisedAgents)
            return of(this.supervisedAgents);

        return this.apiService.post(`${controllerRoute}/supervised-agents`, payload).pipe(
            map((supervisedAgentList: SupervisedAgentList) => {
                this.supervisedAgents = supervisedAgentList.supervisedAgents;
                return this.supervisedAgents;
            }),
        );
    }

    public getScheduledEvents(payload: AgentFilterPayload): Observable<ScheduledEventsList> {
        return this.apiService.post(`${controllerRoute}/scheduled-events`, payload).pipe(
            map((response: ScheduledEventsList) => {
                return response;
            }),
        );
    }

    public getMemberScheduledEvents(payload: AgentFilterPayload): Observable<ScheduledEventsList> {
        return this.apiService.post(`${controllerRoute}/scheduled-events-member`, payload).pipe(
            map((response: ScheduledEventsList) => {
                return response;
            }),
        );
    }

    public createScheduledEvent(payload: ScheduledEventPayload): Observable<ScheduledEvent> {
        return this.apiService.post(`${controllerRoute}/new-scheduled-event`, payload).pipe(
            map((response: ScheduledEvent) => {
                this.toastService.buildToastMessage(ToastType.success, "Saved", "Follow Up Created!");
                return response;
            }),
        );
    }

    public appendScheduledEvent(payload: ScheduledEventAppendPayload): Observable<ScheduledEvent> {
        return this.apiService.post(`${controllerRoute}/append-scheduled-event-note`, payload).pipe(
            map((response: ScheduledEvent) => {
                this.toastService.buildToastMessage(ToastType.success, "Saved", "Follow Up Updated!");
                return response;
            }),
        );
    }

    public getCompletedScheduledEvents(payload: AgentFilterPayload): Observable<ScheduledEventsList> {
        return this.apiService.post(`${controllerRoute}/completed-scheduled-events`, payload).pipe(
            map((response: ScheduledEventsList) => {
                return response;
            }),
        );
    }

    public getMemberCompletedScheduledEvents(payload: AgentFilterPayload): Observable<ScheduledEventsList> {
        return this.apiService.post(`${controllerRoute}/completed-scheduled-events-member`, payload).pipe(
            map((response: ScheduledEventsList) => {
                return response;
            }),
        );
    }

    public setCompletedScheduledEventsToIncomplete(payload: ScheduledEventCompletePayload): Observable<ScheduledEventsList> {
        return this.apiService.post(`${controllerRoute}/set-scheduled-event-as-incomplete`, payload).pipe(
            map((response: ScheduledEventsList) => {
                this.toastService.buildToastMessage(ToastType.success, "Saved", "Follow Up Set To Open!");
                return response;
            }),
        );
    }

    public setScheduledEventsToComplete(payload: ScheduledEventCompletePayload): Observable<void> {
        return this.apiService.post(`${controllerRoute}/complete-scheduled-event`, payload).pipe(
            map((response: any) => {
                this.toastService.buildToastMessage(ToastType.success, "Saved", "Follow Up Set To Complete!");
            }),
        );
    }

    public getScheduledEventsSpecification(): Observable<ScheduledEventsList> {
        return this.apiService.get(`${controllerRoute}/scheduled-events-specification`).pipe(
            map((response: ScheduledEventsList) => {
                return response;
            }),
        );
    }

    public reassignScheduledEventsToAgent(payload: ReassignScheduledEventListPayload): Observable<void> {
        return this.apiService.post(`${controllerRoute}/reassign-agent-assigned-to-scheduled-events`, payload).pipe(
            map((response: any) => {
                this.toastService.buildToastMessage(ToastType.success, "Saved", "Follow Up Reassigned!");
            }),
        );
    }

    public getScheduledEventsSearch(payload: ScheduledEventSearchPayload): Observable<ScheduledEventsList> {
        return this.apiService.post(`${controllerRoute}/scheduled-events-specification`, payload).pipe(
            map((response: ScheduledEventsList) => {
                return response;
            }),
        );
    }

    public getTodaysReports(): Observable<Report[]> {
        return this.apiService.get(`${controllerRoute}/recent-reports`).pipe(
            map((response: ReportsPayload) => {
                return response.reports;
            }),
        );
    }

    public getReportHistory(): Observable<Report[]> {
        return this.apiService.get(`${controllerRoute}/report-history`).pipe(
            map((response: ReportsPayload) => {
                return response.reports;
            }),
        );
    }

    public deleteReport(payload: Report): Observable<any> {
        return this.apiService.post(`${controllerRoute}/delete-report`, payload).pipe(
            map((response: any) => {
                return response;
            }),
        );
    }

    public getReportById(payload: number): Observable<Blob> {
        return this.apiService.get(`${controllerRoute}/get-report/${payload}`, ApiHeaderType.blob).pipe(
            map((response: Blob) => {
                return response;
            }),
        );
    }

    public getMemberActivityLogs(payload: AgentFilterPayload): Observable<ActivityLog[]> {
        return this.apiService.post(`${controllerRoute}/member-activity-log-list`, payload).pipe(
            map((response: MemberActivityLogs) => {
                return response.memberActivityLog;
            }),
        );
    }

    public getMemberActivityLogById(payload: ActivityLogPayloadId): Observable<ActivityLog> {
        return this.apiService.post(`${controllerRoute}/member-activity-log-by-id`, payload).pipe(
            map((response: ActivityLog) => {
                return response;
            }),
        );
    }

    public createMemberActivityLog(payload: ActivityLogCreatePayload): Observable<void> {
        return this.apiService.post(`${controllerRoute}/member-activity-log`, payload).pipe(
            map((response: void) => {
                this.toastService.buildToastMessage(ToastType.success, "Saved", "New Activity Log Created!");
            }),
        );
    }

    public submitActivityLogAppendNote(payload: ActivityLogAppendPayload): Observable<void> {
        return this.apiService.put(`${controllerRoute}/member-activity-log`, payload).pipe(
            map((response: void) => {
                this.toastService.buildToastMessage(ToastType.success, "Saved", "Activity Log Updated!");
            }),
        );
    }

}